// Registry across both brands — the equivalent of
// server/src/utils/timeline/daily-timeline-notifications.ts, keyed the SMP way:
// one key per touchpoint (track prefix + days before renewal).
//
//   A{day}   AUTO         auto-renewing, pay in full → auto-renew/, email only
//   AD{day}  AUTO_DD      auto-renewing, monthly DD  → auto-renew/, email only
//   AC{day}  AUTO_CASH    pay in full, no auto       → pay-in-full/, email + SMS
//   NA{day}  NON_AUTO_DD  monthly Direct Debit       → monthly-dd/, email + SMS
//   WB{day}  win-back, days AFTER a lapse            → win-back-lapse/, MARKETING
//   AN{day}  win-back, days BEFORE next renewal      → win-back-anniversary/, MARKETING
//   REN0     renewal day, documents ready            → renewed/, email only

import type { BrandKey } from "./brands";
import type { EmailData } from "./types";
import * as bcd from "./business-compare-direct";
import * as myb from "./mind-your-business";

export type { Touchpoint } from "./business-compare-direct";

export const BRAND_TEMPLATES: Record<BrandKey, typeof bcd.TEMPLATES> = {
  BUSINESS_COMPARE_DIRECT: bcd.TEMPLATES,
  MIND_YOUR_BUSINESS: myb.TEMPLATES,
};

export type Rendered = { subject: string; html: string };

/**
 * Render one email by key — handy for previews and one-offs. The scheduled sends
 * go through `getDailyTimeLine` in ./timeline, which also carries the SMS copy.
 */
export function render(brandKey: BrandKey, key: string, data: EmailData): Rendered {
  const t = BRAND_TEMPLATES[brandKey]?.[key];
  if (!t) throw new Error(`render: unknown template "${key}" for brand "${brandKey}"`);
  return { subject: t.subject(data), html: t.html(data) };
}

/** The Templates-screen grouping, mirroring the SMP admin view + the folders. */
export const GROUPS: { title: string; folder: string; blurb: string; keys: string[] }[] = [
  {
    title: "Auto-renew",
    folder: "auto-renew",
    blurb:
      "One reassuring email, 21 days out — cash (A21) or monthly Direct Debit (AD21). No pricing and no SMS: the policy renews automatically.",
    keys: ["A21", "AD21"],
  },
  {
    title: "Pay in full (cash)",
    folder: "pay-in-full",
    blurb: "A priced email plus a matching SMS at each step of the chase cadence.",
    keys: ["AC21", "AC14", "AC10", "AC7", "AC3", "AC2", "AC1"],
  },
  {
    title: "Monthly (Direct Debit)",
    folder: "monthly-dd",
    blurb: "Same cadence and copy as cash; the payment card shows the monthly amount (no total payable).",
    keys: ["NA21", "NA14", "NA10", "NA7", "NA3", "NA2", "NA1"],
  },
  {
    title: "Win-back — after lapse",
    folder: "win-back-lapse",
    blurb: "£25 Amazon voucher offers 3 days, 1, 2 and 4 weeks after a policy lapses. Stops once won back.",
    keys: ["WB3", "WB7", "WB14", "WB28"],
  },
  {
    title: "Win-back — next year",
    folder: "win-back-anniversary",
    blurb: "From a month before their renewal comes around again — a fresh chance to win the business back.",
    keys: ["AN30", "AN14", "AN7"],
  },
  {
    title: "Renewed",
    folder: "renewed",
    blurb: "Sent on renewal day once the policy has renewed — points the customer at My Portal.",
    keys: ["REN0"],
  },
];

export { BRANDS, getBrand } from "./brands";
export type { BrandConfig, BrandKey } from "./brands";
export type { EmailData, EmailTemplate, PaymentOverview, PaymentType, QuoteRow } from "./types";

export { default as getDailyTimeLine, getChaseType, toEmailData } from "./timeline";
export type { ChaseType, Stage, TimelineChase, TimelineResult } from "./timeline";
